"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.COMPARISON_STATUS_LABELS = exports.releaseHoldSchema = exports.comparisonMoveSchema = exports.selectCandidateSchema = exports.eliminateSchema = exports.COMPARISON_NOTE_MAX = exports.SELECTION_UNDO_MINUTES = exports.HOLD_WARNING_DAYS = void 0;
exports.placesLeft = placesLeft;
exports.holdWarningAt = holdWarningAt;
exports.holdStateAt = holdStateAt;
exports.holdExpiryFrom = holdExpiryFrom;
exports.acrossRounds = acrossRounds;
exports.daysInProcess = daysInProcess;
exports.isSelectionUndoable = isSelectionUndoable;
const zod_1 = require("zod");
const decisions_1 = require("./decisions");
const schemas_1 = require("./schemas");
// S22 — the comparison view and final selection (DEC-02, F12.08–F12.11). Sources: master
// plan S22 and its planning decisions (DECISIONS-LOG S22 Q1–Q8); E10; Appendix A; §L18;
// §L22 N-55, N-65, N-79; §L23; §L25 Offer.
/** §L22 N-65: the recruiter hears this many days before a hold ends. */
exports.HOLD_WARNING_DAYS = 3;
/** F12.10: a final selection can be taken back this long, while its offer draft is untouched. */
exports.SELECTION_UNDO_MINUTES = 10;
exports.COMPARISON_NOTE_MAX = 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
/** §L18 as clarified in S22: a place nobody has been selected for. Never below nothing. */
function placesLeft(places) {
    return Math.max(0, places.total - places.filled - places.selected);
}
/** When N-65 goes: three days before the hold ends. */
function holdWarningAt(expiresAt) {
    return new Date(expiresAt.getTime() - exports.HOLD_WARNING_DAYS * DAY_MS);
}
/** "Hold ended — decide" once the date passes; "ending soon" from the moment N-65 is due. */
function holdStateAt(expiresAt, now) {
    if (now.getTime() >= expiresAt.getTime())
        return 'ended';
    return now.getTime() >= holdWarningAt(expiresAt).getTime() ? 'ending_soon' : 'active';
}
/** A new hold, or "keep 30 more days": thirty days from now (S22 Q7, Q8) — the length is ADMIN-15's since S31. */
function holdExpiryFrom(now, days = decisions_1.HOLD_EXPIRY_DAYS) {
    return new Date(now.getTime() + days * DAY_MS);
}
// --- Scores across rounds -----------------------------------------------------------------------
/**
 * A cell's figure: each round that scored the line counts once, whatever its panel size,
 * so a five-person round cannot drown out a two-person one. Rounds with no score are left out.
 */
function acrossRounds(roundMeans) {
    const present = roundMeans.filter((mean) => mean !== null);
    if (present.length === 0)
        return null;
    return Math.round((present.reduce((sum, mean) => sum + mean, 0) / present.length) * 100) / 100;
}
/** "Total days in process": calendar days since they applied, to today or to when they left. */
function daysInProcess(appliedAt, until) {
    return Math.max(0, Math.floor((until.getTime() - appliedAt.getTime()) / DAY_MS));
}
/** F12.10's undo: inside the window, and only while nobody has touched the draft. */
function isSelectionUndoable(selectedAt, now, isDraftUntouched) {
    return isDraftUntouched && now.getTime() - selectedAt.getTime() <= exports.SELECTION_UNDO_MINUTES * 60_000;
}
// --- Inputs -----------------------------------------------------------------------------------
const versionField = zod_1.z.number().int().nonnegative();
const noteField = zod_1.z.string().trim().max(exports.COMPARISON_NOTE_MAX, `Notes can be up to ${exports.COMPARISON_NOTE_MAX} characters.`).nullable().default(null);
const poolNameSchema = zod_1.z.string().trim().min(1, 'Give the pool a name.').max(80, 'Pool names can be up to 80 characters.');
/** F12.09 — a reason is mandatory; eliminating only hides (S22 Q6). */
exports.eliminateSchema = zod_1.z.object({
    reasonCode: zod_1.z.string().trim().min(1, 'Pick a reason before eliminating.').max(40),
    note: noteField,
});
/** R8 (F24.11): "Select" from the last round's result — the application and the version the person read. */
exports.selectCandidateSchema = zod_1.z.object({ applicationId: schemas_1.uuidSchema, version: versionField });
/** F12.10 and F12.07 from DEC-02: the application's version the person read. */
exports.comparisonMoveSchema = zod_1.z.object({ version: versionField });
/** F12.11 — release to offer needs a free place; release to pool a pool-eligible reason and a pool. */
exports.releaseHoldSchema = zod_1.z.discriminatedUnion('to', [
    zod_1.z.object({ to: zod_1.z.literal('offer'), version: versionField }),
    zod_1.z.object({
        to: zod_1.z.literal('pool'),
        version: versionField,
        reasonCode: zod_1.z.string().trim().min(1, 'Pick a reason before releasing.').max(40),
        poolIds: zod_1.z.array(schemas_1.uuidSchema).max(10).default([]),
        newPoolNames: zod_1.z.array(poolNameSchema).max(5).default([]),
    }),
]).superRefine((input, ctx) => {
    if (input.to === 'pool' && input.poolIds.length + input.newPoolNames.length === 0) {
        ctx.addIssue({ code: 'custom', path: ['poolIds'], message: 'Choose a pool to release them to.' });
    }
});
exports.COMPARISON_STATUS_LABELS = {
    finalist: 'Passed the final round',
    added: 'Added to compare',
    held: 'On hold',
    selected: 'Selected',
};
//# sourceMappingURL=comparison.js.map